import { Card, Button, Skeleton, Progress } from "@dawa/ui";
import { Gauge } from "lucide-react";

const SUCCESS_THRESHOLD = 70;

function variantFor(v: number) {
  return v >= 75 ? "success" : v >= 50 ? "warning" : ("danger" as const);
}

function labelFor(key: string) {
  return key.replace(/[_-]/g, " ");
}

function statusFor(value: number) {
  const ok = value >= SUCCESS_THRESHOLD;
  return {
    ok,
    label: ok ? "Respecté" : "À améliorer",
    className: ok ? "text-success" : "text-danger",
  };
}

export function ScoreCard({
  score,
  loading = false,
  onGenerate,
}: {
  score: any;
  loading?: boolean;
  onGenerate?: () => void;
}) {
  if (loading) {
    return (
      <Card className="space-y-3 p-4">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Gauge className="h-4 w-4 text-muted" /> Score
        </h2>
        <Skeleton lines={3} />
      </Card>
    );
  }

  if (!score || typeof score !== "object") {
    return (
      <Card className="space-y-3 p-4">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Gauge className="h-4 w-4 text-muted" /> Score
        </h2>
        <div className="text-sm text-muted">Aucun score disponible</div>
        {onGenerate && (
          <Button type="button" onClick={onGenerate} className="w-max">
            Calculer le score
          </Button>
        )}
      </Card>
    );
  }

  const totalScore = typeof score.total === "number" ? score.total : null;
  const breakdown = score.breakdown && typeof score.breakdown === "object" ? score.breakdown : null;
  const breakdownEntries = breakdown
    ? Object.entries(breakdown).filter(([, v]) => typeof v === "number")
    : [];
  const fallbackEntries = Object.entries(score).filter(([, v]) => typeof v === "number");
  const entries = breakdownEntries.length > 0 ? breakdownEntries : fallbackEntries;

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Gauge className="h-4 w-4 text-muted" /> Score
        </h2>
        {score && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-label="Exporter score JSON"
            onClick={() => {
              try {
                const json = JSON.stringify(score, null, 2);
                const blob = new Blob([json], { type: "application/json;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `score.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch {
                /* ignore */
              }
            }}
          >
            Exporter JSON
          </Button>
        )}
      </div>

      {totalScore !== null && (
        <div className="space-y-2 rounded-md border border-border/60 bg-muted/20 p-3">
          <div className="flex items-center justify-between text-sm font-medium">
            <span className="text-text">Score total</span>
            <span className="text-text">{totalScore}/100</span>
          </div>
          <Progress value={totalScore} variant={variantFor(totalScore)} size="sm" showLabel />
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-sm text-muted">—</div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wide text-muted">Critères</div>
          {entries.map(([k, v]) => {
            const value = Number(v);
            const status = statusFor(value);
            return (
              <div key={k} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="capitalize text-text">{labelFor(k)}</span>
                  <span className={`text-xs font-medium ${status.className}`}>{status.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={value} variant={variantFor(value)} size="sm" showLabel />
                  <span className="text-xs text-muted">{value}/100</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
